/**
 * Admin console translations.
 *
 * Covers the internal staff screens: AdminNotificationPanel (anp),
 * AdminWrapper (aw), AppWalkthrough chrome (awt) and AboutUsView (abt).
 *
 * The company and its staff are German-speaking, so the admin console is
 * translated too — not just the investor-facing app.
 *
 * NOT translated on purpose (see notes in the components):
 *   - Notification/story CONTENT the admin types (title/message/description
 *     EN + DE fields) and the auto-generated update text: that is what the
 *     recipient reads, not UI chrome.
 *   - Placeholders that are examples of the content for a specific language
 *     field (the English example under "Title (English)" stays English).
 *   - Brand/domain terms: InvestMenter, Asset Desk, AI, DLD, Ejari, AED, EUR,
 *     WhatsApp, Push, Stories, ROI.
 */
import { AreaDict } from './index';

export const adminDict: AreaDict = {
  en: {
    // ---------- AdminWrapper ----------
    awAccessDenied: 'Access Denied',
    awNoPermission: "You don't have permission to access the admin panel.",
    awGoBack: 'Go Back',
    awBackToPortal: 'Back to Portal',
    awAdminPanel: 'Admin Panel',

    // ---------- AppWalkthrough chrome ----------
    awtHighlightedAbove: 'Highlighted Above ↑',
    awtSkipTour: 'Skip Tour',
    awtNext: 'Next',
    awtFinish: 'Finish',

    // ---------- AboutUsView ----------
    abtExcellent: 'EXCELLENT',

    // ---------- AdminNotificationPanel: header & tabs ----------
    anpTitle: 'Notification Center',
    anpTabCreate: 'Create',
    anpTabAppUpdate: 'App Update',
    anpTabStories: 'Stories',
    anpTabHistory: 'History',
    anpTabPortfolioEmail: 'Portfolio Email',

    // ---------- Notification types ----------
    anpTypeAnnouncement: 'Announcement',
    anpTypeWebinar: 'Webinar',
    anpTypeJourney: 'Dubai Journey',
    anpTypeEvent: 'Event',
    anpTypePayment: 'Payment',
    anpTypeDocument: 'Document',
    anpTypeConstruction: 'Construction',
    anpTypeReminder: 'Reminder',

    // ---------- Create tab ----------
    anpNotificationType: 'Notification Type',
    anpTitleEnRequired: 'Title (English) *',
    anpTitleEnPlain: 'Title (English)',
    anpTitleDeLabel: 'Title (German)',
    anpMessageEnRequired: 'Message (English) *',
    anpMessageDeLabel: 'Message (German)',
    anpPriority: 'Priority',
    anpPriorityLow: 'Low',
    anpPriorityMedium: 'Medium',
    anpPriorityHigh: 'High',
    anpSendTo: 'Send To',
    anpAllInvestors: 'All Investors',
    anpSpecificInvestor: 'Specific Investor',
    anpSelectInvestor: 'Select an investor...',
    anpActionLink: 'Action Link (Optional)',
    anpActionNone: 'None',
    anpActionAppTab: 'App Tab',
    anpActionExternalUrl: 'External URL',
    anpSelectTab: 'Select a tab...',
    anpLinkMarketplace: 'Marketplace',
    anpSending: 'Sending...',
    anpSendNotification: 'Send Notification',
    anpSentSuccess: '✓ Notification sent successfully!',
    anpSendFailed: 'Failed to send notification',
    anpDeleteNotificationConfirm: 'Delete this notification?',

    // ---------- App Update tab ----------
    anpNotificationPreview: 'Notification Preview',
    anpAppUpdateBadge: 'APP UPDATE',
    anpAppUpdateBannerAlt: 'Asset Desk App Update',
    anpUpdateDetails: 'Update Details',
    anpAppVersion: 'App Version (Optional)',
    anpAppVersionPh: 'e.g., 2.5.0',
    anpWhatsNew: "What's New (up to 3 highlights)",
    anpFeaturePh1: 'e.g., Improved portfolio dashboard',
    anpFeaturePh2: 'e.g., New cashflow analytics',
    anpFeaturePh3: 'e.g., Enhanced security features',
    anpCustomMessageEn: 'Custom Message EN (Optional - overrides auto-generated)',
    anpCustomMessageDe: 'Custom Message DE (Optional)',
    anpLeaveEmptyAutoLong: 'Leave empty to auto-generate from features above...',
    anpLeaveEmptyAutoShort: 'Leave empty for automatic generation...',
    anpSendingToAllInvestors: 'Sending to All Investors...',
    anpNotifyAllInvestors: 'Notify All Investors',
    anpHighPriorityHint: 'This will send a high-priority notification to all registered investors',
    anpUpdateSent: 'App update notification sent to all investors!',
    anpUpdateSendFailed: 'Failed to send app update notification',
    anpSendFailedShort: 'Failed to send',

    // ---------- Stories tab ----------
    anpCatListing: 'New Listing',
    anpCatUnit: 'Available Unit',
    anpCatOffer: 'Special Offer',
    anpCatUpdate: 'Update',
    anpCatFeatured: 'Featured',
    anpCreateNewStory: 'Create New Story',
    anpStoryCategory: 'Story Category',
    anpImageUrl: 'Image URL *',
    anpImageUrlPh: 'https://drive.google.com/file/d/... or direct image URL',
    anpDescriptionEn: 'Description (English)',
    anpDescriptionDe: 'Description (German)',
    anpPrice: 'Price',
    anpButtonLabel: 'Button Label',
    anpButtonLabelPh: 'e.g., Learn More, Book Now',
    anpButtonUrl: 'Button URL',
    anpStoryDuration: 'Story Duration',
    anpDuration24h: '24 Hours',
    anpDuration48h: '48 Hours',
    anpDuration3d: '3 Days',
    anpDuration7d: '7 Days',
    anpDuration14d: '14 Days',
    anpDuration30d: '30 Days',
    anpPublishing: 'Publishing...',
    anpPublishStory: 'Publish Story',
    anpActiveStories: 'Active Stories',
    anpRefresh: 'Refresh',
    anpNoStories: 'No stories yet. Create your first story above!',
    anpExpiredBadge: 'EXPIRED',
    anpViews: 'views',
    anpStoryFieldsRequired: 'Title and image URL are required',
    anpStoryPublished: 'Story published successfully!',
    anpStoryCreateFailed: 'Failed to create story',
    anpDeleteStoryConfirm: 'Delete this story?',

    // ---------- History tab ----------
    anpRecentNotifications: 'Recent Notifications',
    anpNoNotifications: 'No notifications yet',
    anpRead: 'Read',
    anpUnread: 'Unread',

    // ---------- Portfolio Email tab ----------
    anpPeTitle: 'AI-Generated Portfolio Email',
    anpPeSubtitle:
      'Generate and send personalized portfolio reports to investors. Each email includes an AI-written summary, property details, market data, recommendations, and webinar invitations.',
    anpPeSendSpecific: 'Send to Specific Investor',
    anpPeSelectInvestor: 'Select Investor...',
    anpPreview: 'Preview',
    anpSendEmail: 'Send Email',
    anpPeProperties: 'properties',
    anpPeTotalValue: 'Total Value',
    anpPeSendAll: 'Send to All Investors',
    anpPeSendAllDesc:
      'Generates and sends a personalized portfolio email to every investor with at least one property. This uses AI to create unique content for each investor. May take several minutes.',
    anpPeSendingAll: 'Sending to all investors...',
    anpPeAutoTitle: 'Automatic Monthly Emails',
    anpPeAutoBody:
      'Portfolio reports are automatically sent to all investors on the 1st of each month at 9:00 AM Dubai time. No action needed.',
    anpPeEmailPreview: 'Email Preview',
    anpPeOpenInNewTab: 'Open in New Tab',
    anpPeIframeTitle: 'Portfolio Email Preview',
    anpPePreviewOk: 'Preview generated successfully',
    anpPePreviewFailed: 'Failed to generate preview',
    anpPeSendConfirm: 'Send portfolio email to this investor?',
    anpPeEmailFailed: 'Failed to send email',
    anpPeSendAllConfirm: 'Send portfolio emails to ALL investors with properties? This may take several minutes.',
    anpPeBulkFailed: 'Bulk send failed',
  },
  de: {
    // ---------- AdminWrapper ----------
    awAccessDenied: 'Zugriff verweigert',
    awNoPermission: 'Sie haben keine Berechtigung für den Zugriff auf den Administrationsbereich.',
    awGoBack: 'Zurück',
    awBackToPortal: 'Zurück zum Portal',
    awAdminPanel: 'Administrationsbereich',

    // ---------- AppWalkthrough chrome ----------
    awtHighlightedAbove: 'Oben hervorgehoben ↑',
    awtSkipTour: 'Tour überspringen',
    awtNext: 'Weiter',
    awtFinish: 'Fertig',

    // ---------- AboutUsView ----------
    abtExcellent: 'HERVORRAGEND',

    // ---------- AdminNotificationPanel: header & tabs ----------
    anpTitle: 'Benachrichtigungszentrale',
    anpTabCreate: 'Erstellen',
    anpTabAppUpdate: 'App-Update',
    anpTabStories: 'Stories',
    anpTabHistory: 'Verlauf',
    anpTabPortfolioEmail: 'Portfolio-E-Mail',

    // ---------- Notification types ----------
    anpTypeAnnouncement: 'Ankündigung',
    anpTypeWebinar: 'Webinar',
    anpTypeJourney: 'Dubai-Reise',
    anpTypeEvent: 'Veranstaltung',
    anpTypePayment: 'Zahlung',
    anpTypeDocument: 'Dokument',
    anpTypeConstruction: 'Bau',
    anpTypeReminder: 'Erinnerung',

    // ---------- Create tab ----------
    anpNotificationType: 'Benachrichtigungstyp',
    anpTitleEnRequired: 'Titel (Englisch) *',
    anpTitleEnPlain: 'Titel (Englisch)',
    anpTitleDeLabel: 'Titel (Deutsch)',
    anpMessageEnRequired: 'Nachricht (Englisch) *',
    anpMessageDeLabel: 'Nachricht (Deutsch)',
    anpPriority: 'Priorität',
    anpPriorityLow: 'Niedrig',
    anpPriorityMedium: 'Mittel',
    anpPriorityHigh: 'Hoch',
    anpSendTo: 'Senden an',
    anpAllInvestors: 'Alle Investoren',
    anpSpecificInvestor: 'Bestimmter Investor',
    anpSelectInvestor: 'Investor auswählen...',
    anpActionLink: 'Aktions-Link (Optional)',
    anpActionNone: 'Keiner',
    anpActionAppTab: 'App-Tab',
    anpActionExternalUrl: 'Externe URL',
    anpSelectTab: 'Tab auswählen...',
    anpLinkMarketplace: 'Marktplatz',
    anpSending: 'Wird gesendet...',
    anpSendNotification: 'Benachrichtigung senden',
    anpSentSuccess: '✓ Benachrichtigung erfolgreich gesendet!',
    anpSendFailed: 'Benachrichtigung konnte nicht gesendet werden',
    anpDeleteNotificationConfirm: 'Diese Benachrichtigung löschen?',

    // ---------- App Update tab ----------
    anpNotificationPreview: 'Vorschau der Benachrichtigung',
    anpAppUpdateBadge: 'APP-UPDATE',
    anpAppUpdateBannerAlt: 'Asset Desk App-Update',
    anpUpdateDetails: 'Update-Details',
    anpAppVersion: 'App-Version (Optional)',
    anpAppVersionPh: 'z. B. 2.5.0',
    anpWhatsNew: 'Was ist neu (bis zu 3 Highlights)',
    anpFeaturePh1: 'z. B. Verbessertes Portfolio-Dashboard',
    anpFeaturePh2: 'z. B. Neue Cashflow-Analysen',
    anpFeaturePh3: 'z. B. Erweiterte Sicherheitsfunktionen',
    anpCustomMessageEn: 'Eigene Nachricht EN (Optional – überschreibt die automatische Erstellung)',
    anpCustomMessageDe: 'Eigene Nachricht DE (Optional)',
    anpLeaveEmptyAutoLong: 'Leer lassen, um automatisch aus den Highlights oben zu erstellen...',
    anpLeaveEmptyAutoShort: 'Leer lassen für automatische Erstellung...',
    anpSendingToAllInvestors: 'Wird an alle Investoren gesendet...',
    anpNotifyAllInvestors: 'Alle Investoren benachrichtigen',
    anpHighPriorityHint:
      'Dies sendet eine Benachrichtigung mit hoher Priorität an alle registrierten Investoren',
    anpUpdateSent: 'App-Update-Benachrichtigung an alle Investoren gesendet!',
    anpUpdateSendFailed: 'App-Update-Benachrichtigung konnte nicht gesendet werden',
    anpSendFailedShort: 'Senden fehlgeschlagen',

    // ---------- Stories tab ----------
    anpCatListing: 'Neues Angebot',
    anpCatUnit: 'Verfügbare Einheit',
    anpCatOffer: 'Sonderangebot',
    anpCatUpdate: 'Update',
    anpCatFeatured: 'Empfohlen',
    anpCreateNewStory: 'Neue Story erstellen',
    anpStoryCategory: 'Story-Kategorie',
    anpImageUrl: 'Bild-URL *',
    anpImageUrlPh: 'https://drive.google.com/file/d/... oder direkte Bild-URL',
    anpDescriptionEn: 'Beschreibung (Englisch)',
    anpDescriptionDe: 'Beschreibung (Deutsch)',
    anpPrice: 'Preis',
    anpButtonLabel: 'Button-Beschriftung',
    anpButtonLabelPh: 'z. B. Mehr erfahren, Jetzt buchen',
    anpButtonUrl: 'Button-URL',
    anpStoryDuration: 'Story-Dauer',
    anpDuration24h: '24 Stunden',
    anpDuration48h: '48 Stunden',
    anpDuration3d: '3 Tage',
    anpDuration7d: '7 Tage',
    anpDuration14d: '14 Tage',
    anpDuration30d: '30 Tage',
    anpPublishing: 'Wird veröffentlicht...',
    anpPublishStory: 'Story veröffentlichen',
    anpActiveStories: 'Aktive Stories',
    anpRefresh: 'Aktualisieren',
    anpNoStories: 'Noch keine Stories. Erstellen Sie oben Ihre erste Story!',
    anpExpiredBadge: 'ABGELAUFEN',
    anpViews: 'Aufrufe',
    anpStoryFieldsRequired: 'Titel und Bild-URL sind erforderlich',
    anpStoryPublished: 'Story erfolgreich veröffentlicht!',
    anpStoryCreateFailed: 'Story konnte nicht erstellt werden',
    anpDeleteStoryConfirm: 'Diese Story löschen?',

    // ---------- History tab ----------
    anpRecentNotifications: 'Aktuelle Benachrichtigungen',
    anpNoNotifications: 'Noch keine Benachrichtigungen',
    anpRead: 'Gelesen',
    anpUnread: 'Ungelesen',

    // ---------- Portfolio Email tab ----------
    anpPeTitle: 'AI-generierte Portfolio-E-Mail',
    anpPeSubtitle:
      'Erstellen und senden Sie personalisierte Portfolio-Berichte an Investoren. Jede E-Mail enthält eine AI-generierte Zusammenfassung, Immobiliendetails, Marktdaten, Empfehlungen und Webinar-Einladungen.',
    anpPeSendSpecific: 'An bestimmten Investor senden',
    anpPeSelectInvestor: 'Investor auswählen...',
    anpPreview: 'Vorschau',
    anpSendEmail: 'E-Mail senden',
    anpPeProperties: 'Immobilien',
    anpPeTotalValue: 'Gesamtwert',
    anpPeSendAll: 'An alle Investoren senden',
    anpPeSendAllDesc:
      'Erstellt und sendet eine personalisierte Portfolio-E-Mail an jeden Investor mit mindestens einer Immobilie. Dabei erstellt AI für jeden Investor individuelle Inhalte. Dies kann mehrere Minuten dauern.',
    anpPeSendingAll: 'Wird an alle Investoren gesendet...',
    anpPeAutoTitle: 'Automatische monatliche E-Mails',
    anpPeAutoBody:
      'Portfolio-Berichte werden automatisch am 1. jedes Monats um 9:00 Uhr Dubai-Zeit an alle Investoren gesendet. Es ist keine Aktion erforderlich.',
    anpPeEmailPreview: 'E-Mail-Vorschau',
    anpPeOpenInNewTab: 'In neuem Tab öffnen',
    anpPeIframeTitle: 'Vorschau der Portfolio-E-Mail',
    anpPePreviewOk: 'Vorschau erfolgreich erstellt',
    anpPePreviewFailed: 'Vorschau konnte nicht erstellt werden',
    anpPeSendConfirm: 'Portfolio-E-Mail an diesen Investor senden?',
    anpPeEmailFailed: 'E-Mail konnte nicht gesendet werden',
    anpPeSendAllConfirm:
      'Portfolio-E-Mails an ALLE Investoren mit Immobilien senden? Dies kann mehrere Minuten dauern.',
    anpPeBulkFailed: 'Massenversand fehlgeschlagen',
  },
};
